function parseTimeString(time: string) {
  return Number(time.split(':')[0]);
}

/**
 * Converts an adjusted date back to UTC for saving to the server
 * Note that the use of getTimezoneOffset must occur on the date to be converted and not the current date to account for changes in timezone offset across DST boundaries
 *
 * Example: For a UTC-8 timezone with no additional offset, `2025-12-09T22:00:00Z` (adjusted date showing 2:00 PM in local time) is converted back to `2025-12-09T14:00:00Z` (UTC).
 * @param localDateObject - The adjusted date object from DatePicker that displays the nominal time in the user's local timezone
 * @param offsetMs - The (usually timezone) offset in milliseconds that was used when adjusting the date. This should match the offsetMs used in adjustToSameNominalTimeForUser
 * @returns A Date object in UTC that represents the same nominal time that was displayed to the user.
 */
export function adjustFromSameNominalTimeForUser(localDateObject: Date, offsetMs = 0): Date {
  return new Date(localDateObject.getTime() - 60000 * localDateObject.getTimezoneOffset() - offsetMs);
}

/**
 * Allows a user to edit a UTC date/time without it being automatically shown as local time by DatePicker
 * Note that the use of getTimezoneOffset must occur on the date to be converted and not the current date to account for changes in timezone offset across DST boundaries
 *
 * Example: For a UTC-8 timezone with no additional offset, `2025-12-09T14:00:00Z` is adjusted to `2025-12-09T22:00:00Z`.
 * @param value - The date/time to adjust
 * @param offsetMs - The (usually timezone) offset in milliseconds to adjust the date/time by. This can be used to adjust the nominal time by a specified amount (e.g. to account for a timezone that is not a whole hour offset from UTC)
 * @returns A Date object adjusted so that when interpreted in the user's local timezone, it displays the same hour:minute as the original UTC time
 */
export function adjustToSameNominalTimeForUser(value: string | number, offsetMs = 0): Date {
  const date = new Date(value);
  date.setTime(date.getTime() + 60000 * date.getTimezoneOffset() + offsetMs);
  return date;
}

/**
 * @param start - The 24-hour format start time string
 * @param end - The 24-hour format end time string
 * @returns True if the current hour of the user's timezone is between the start and end times, false otherwise
 */
export function betweenHours(start: string, end: string) {
  const [startHour, endHour] = [parseTimeString(start), parseTimeString(end)];
  const currentHour = new Date().getHours();

  // this would be that the time goes from late PM to early AM
  if (startHour >= endHour) {
    return currentHour >= startHour;
  }

  return currentHour >= startHour && currentHour < endHour;
}

/**
 * Converts seconds to hours and minutes
 * @param seconds Seconds value
 * @returns An object with hours and minutes
 */
export function getHoursAndMinutes(seconds: number): { hours: number; minutes: number } {
  const totalMinutes = seconds / 60;
  const hours = Math.floor(totalMinutes / 60) || 0;
  const minutes = Math.round(totalMinutes - hours * 60) || 0;
  return { hours, minutes };
}

export function getMinutesAgoFromDate(date: Date) {
  return getSecondsAgoFromDate(date) / 60;
}

export function getMinutesAgoFromISO(iso: string) {
  return getSecondsAgoFromISO(iso) / 60;
}

export function getSecondsAgoFromDate(date: Date) {
  return (Date.now() - date.getTime()) / 1000;
}

export function getSecondsAgoFromISO(iso: string) {
  return (Date.now() - Date.parse(iso)) / 1000;
}

/**
 * A time-zone aware check to see if a date is today.
 * @param potentialDate Date or string to check if it is today
 * @returns True if the date is today, false otherwise
 */
export function isToday(potentialDate: Date | string): boolean {
  const date = new Date(potentialDate);
  const today = new Date();

  return date.getDate() === today.getDate() && date.getMonth() === today.getMonth() && !!date.getFullYear() && !!today.getFullYear();
}
